import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/cn";
import { useAuth } from "@/auth/AuthContext";
import { FirmBrandingCard } from "@/pages/settings/FirmBrandingCard";
import { FunderCard } from "@/pages/settings/FunderCard";
import { PresetsCard } from "@/pages/settings/PresetsCard";
import { ActivityTemplatesCard } from "@/pages/settings/ActivityTemplatesCard";
import { LawPayCard } from "@/pages/settings/LawPayCard";
import { AcsaEmailCard } from "@/pages/settings/AcsaEmailCard";
import { ResendTestCard } from "@/pages/settings/ResendTestCard";
import { AlertSettingsCard } from "@/pages/settings/AlertSettingsCard";
import { ShareWelcomeCard } from "@/pages/settings/ShareWelcomeCard";
import { ClientIntakesCard } from "@/pages/settings/ClientIntakesCard";
import { IntakeOptionsCard } from "@/pages/settings/IntakeOptionsCard";
import { BackupCard } from "@/pages/settings/BackupCard";
import { MaintenanceCard } from "@/pages/settings/MaintenanceCard";

const TABS = [
  { id: "firm", label: "Firm Profile", owner: false, render: () => <FirmBrandingCard /> },
  {
    id: "billing", label: "Billing", owner: false,
    render: () => (<><FunderCard /><PresetsCard /><ActivityTemplatesCard /></>),
  },
  { id: "payments", label: "Payments — LawPay", owner: false, render: () => <LawPayCard /> },
  {
    id: "emails", label: "Emails & Reminders", owner: false,
    render: () => (<><AcsaEmailCard /><ResendTestCard /><AlertSettingsCard /></>),
  },
  {
    id: "intake", label: "Client Intake", owner: true,
    render: () => (<><ShareWelcomeCard /><ClientIntakesCard /><IntakeOptionsCard /></>),
  },
  {
    id: "data", label: "Data & Backups", owner: true,
    render: () => (<><BackupCard /><MaintenanceCard /></>),
  },
];

export function SettingsPage() {
  const { user } = useAuth();
  const isOwner = Boolean(user?.is_owner);
  const tabs = useMemo(() => TABS.filter((t) => !t.owner || isOwner), [isOwner]);

  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const active = tabs.find((t) => t.id === requested) || tabs[0];

  const setTab = (id) => setParams({ tab: id }, { replace: true });

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">Settings</h1>
        <p className="mt-2 text-sm text-muted-foreground">Firm, billing, payments, emails, client intake and data — grouped into tabs.</p>
      </div>

      <div className="overflow-x-auto border-b border-border" data-testid="settings-tabs">
        <div className="flex min-w-max gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`settings-tab-${t.id}`}
              aria-current={active.id === t.id ? "page" : undefined}
              className={cn(
                "-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                active.id === t.id
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6" data-testid={`settings-section-${active.id}`}>
        {active.render()}
      </div>
    </div>
  );
}
